import { HttpEventType } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { MfDocumentType } from '../../../_shared/enums/document-type.enum';
import { DocumentViewerDocument } from '../../../_shared/model/document.model';
import { PortfolioPaper } from '../../../_shared/model/portfolio/portfolioPaper.model';
import { MfDocument } from '../../../_shared/model/utils/document.model';
import { MergeDocumentsRequestDto } from '../../../_shared/model/utils/merge-documents-request-dto';
import { PaperNamePipe } from '../../../_shared/pipes/paper-name.pipe';
import { MfToastService } from '../../../_shared/services';
import { BaseState } from '../../../_shared/state/base/base.state';
import { DocumentReloadModel } from '../../pages/home/terms/visit/selected-term/model/document-reload-model';
import { TermCommunicationsService } from '../../pages/home/terms/visit/selected-term/terms-communications.service';
import { DocumentsService, ResultService } from '../../pages/home/_services';

@Component({
    selector: 'app-merge-documents',
    templateUrl: './merge-documents.component.html',
    styleUrls: ['./merge-documents.component.scss'],
    providers: [PaperNamePipe],
})
export class MergeDocumentsComponent implements OnInit, OnDestroy {
    @Input() paper: PortfolioPaper;
    @Input() preReservationId: string;
    @Input() visitId: string;
    @Input() termCommunicationsService: TermCommunicationsService;
    public fcFileName = new FormControl(null, [Validators.required]);
    public fcSelectedDocument = new FormControl(null, [Validators.required]);
    public documentList$: Observable<DocumentViewerDocument[]>;
    public isLoading$ = new Subject<boolean>();
    public subscriptions = new Subscription();

    constructor(
        public activeModal: NgbActiveModal,
        private store: Store,
        private paperNamePipe: PaperNamePipe,
        private mfToastService: MfToastService,
        private resultService: ResultService,
        private documentsService: DocumentsService,
    ) {}
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnInit(): void {
        this.documentList$ = this.createDocumentList$();
        this.fcFileName.disable();
        this.initSelectedDocumentChangeListener();
    }

    public onMergeClick(): void {
        of(this.store.selectSnapshot(BaseState.activeContractorId))
            .pipe(
                tap(() => {
                    this.isLoading$.next(true);
                    this.fcFileName.disable();
                }),
                switchMap(contractorId => {
                    const requestDto = {
                        fileName: this.fcFileName.value,
                        documents: [
                            {
                                documentId: (this.fcSelectedDocument.value as DocumentViewerDocument).documentId,
                                documentType: MfDocumentType.DOCUMENT,
                            },
                            {
                                documentId: this.paper.id,
                                documentType: MfDocumentType.PORTFOLIO_PAPER,
                            },
                        ] as MfDocument[],
                    } as MergeDocumentsRequestDto;
                    return this.resultService.mergeDocuments(contractorId, requestDto).pipe(
                        filter(res => res.type !== 0),
                        map(res => {
                            return new Blob([res.body], { type: 'application/pdf' });
                        }),
                        switchMap((res: any) => {
                            const fileName = this.fcFileName.value;
                            const file = new File([res], `${fileName}.pdf`, { type: 'application/pdf' });
                            return this.documentsService.uploadFile(file, fileName).pipe(
                                filter(event => event.type === HttpEventType.Response),
                                take(1),
                                switchMap(uploadRes => {
                                    if (uploadRes && uploadRes.body) {
                                        const docId = uploadRes.body;
                                        return this.documentsService.addFileToVisit(this.paper.visitId, docId);
                                    }
                                    throwError('Error');
                                }),
                            );
                        }),
                        catchError(() => {
                            this.mfToastService.error('Prišlo je do napake.');
                            return throwError('Error');
                        }),
                    );
                }),
                finalize(() => {
                    this.isLoading$.next(false);
                    this.fcFileName.enable();
                }),
            )
            .subscribe(() => {
                this.termCommunicationsService.reloadDocumentsSubject.next({
                    preReservationId: this.preReservationId,
                    visitId: this.visitId,
                } as DocumentReloadModel);
                this.activeModal.close();
                this.mfToastService.success('Dokument uspešno združen.');
            });
    }

    public createDocumentList$(): Observable<DocumentViewerDocument[]> {
        const documents$ = this.termCommunicationsService.createDocumentList$().pipe(
            map(documents => {
                if (documents.length) {
                    return documents.map(document => {
                        return { ...document, isReadOnly: true } as DocumentViewerDocument;
                    });
                }
                return documents;
            }),
        );
        return documents$;
    }

    public initSelectedDocumentChangeListener(): void {
        this.subscriptions.add(
            this.fcSelectedDocument.valueChanges.subscribe((doc: DocumentViewerDocument) => {
                if (doc) {
                    this.fcFileName.enable();
                    if (!this.fcFileName.valid) {
                        const fileName = `${this.paperNamePipe.transform(this.paper?.paperType)} - ${doc.description}`;
                        this.fcFileName.setValue(fileName);
                    }
                } else {
                    this.fcFileName.disable();
                }
            }),
        );
    }
}
